import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import { useCart } from '../../contexts/CartContext';
import { Truck, ShoppingBag, Clock, MapPin, CreditCard } from 'lucide-react';

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const { business, settings, isBusinessOpen, getTodayHours } = useBusiness();
  const { setDeliveryMode, routePrefix } = useCart();

  const handleModeSelect = (mode) => {
    if (mode === 'delivery' && settings && !settings.accepts_delivery) return;
    if (mode === 'takeaway' && settings && !settings.accepts_takeaway) return;
    setDeliveryMode(mode);
    navigate(`${routePrefix}/${mode}`);
  };

  // Usar colores personalizados del negocio o defaults
  const primaryColor = business?.primary_color || '#FFD700';
  const bgColor = business?.background_color || '#0D0D0D';
  const textColor = business?.text_color || '#FFFFFF';
  const fontHeading = business?.font_heading || 'Playfair Display, serif';

  const todayHours = getTodayHours();
  const isOpen = isBusinessOpen();

  const dayLabels = {
    monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
    thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo'
  };

  return (
    <div 
      className="min-h-screen flex flex-col relative"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      {/* Estado Abierto/Cerrado */}
      <div className="absolute top-4 right-4 z-20">
        <div 
          className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 ${
            isOpen ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}
          data-testid="business-status"
        >
          <div className={`w-2 h-2 rounded-full ${isOpen ? 'bg-green-400' : 'bg-red-400'} animate-pulse`}></div>
          {isOpen ? 'Abierto' : 'Cerrado'}
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <img
          src={business.hero_image || 'https://images.unsplash.com/photo-1673166516558-3f1b88a22db8?crop=entropy&cs=srgb&fm=jpg&q=85'}
          alt={business.name}
          className="w-full h-full object-cover"
        />
        <div 
          className="absolute inset-0" 
          style={{ 
            background: `linear-gradient(to bottom, transparent 0%, ${bgColor}dd 70%, ${bgColor} 100%)` 
          }}
        ></div>
      </div>

      {/* Content */}
      <div className="flex-1 -mt-32 relative z-10 px-6 py-6">
        {/* Logo */}
        {business.logo_url && (
          <div className="flex justify-center mb-4">
            <img
              src={business.logo_url}
              alt={`${business.name} logo`}
              className="h-20 w-20 object-contain rounded-full bg-white/10 backdrop-blur-sm shadow-2xl p-2"
            />
          </div>
        )}

        {/* Business Name */}
        <h1 
          className="text-4xl md:text-5xl font-bold text-center mb-2 tracking-tight"
          style={{ fontFamily: fontHeading, color: primaryColor }}
          data-testid="business-name"
        >
          {business.name}
        </h1>

        {/* Info Rápida: Horario y Tiempo de Entrega */}
        <div className="flex flex-wrap justify-center gap-3 mb-6 text-xs">
          {todayHours && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10">
              <Clock className="w-3.5 h-3.5" style={{ color: primaryColor }} />
              <span>
             {todayHours.periods && todayHours.periods.length > 0 ? (
          `Hoy: ${todayHours.periods.map(p => `${p.open_time} - ${p.close_time}`).join(' & ')}` 
        ) : (
          todayHours.is_open ? `Hoy: ${todayHours.open_time} - ${todayHours.close_time}` : 'Hoy: Cerrado'
        )}
              </span>
            </div>
          )}
          {settings?.estimated_delivery_time && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10">
              <Truck className="w-3.5 h-3.5" style={{ color: primaryColor }} />
              <span>{settings.estimated_delivery_time}</span>
            </div>
          )}
          {settings?.min_order_amount > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10">
              <span>Pedido mín: ${settings.min_order_amount}</span>
            </div>
          )}
        </div>

        <p className="text-center text-sm opacity-75 mb-6" data-testid="welcome-message">
          Seleccioná cómo preferís recibir tu pedido
        </p>

        {/* Mode Selection */}
        <div className="space-y-3 max-w-md mx-auto mb-6">
          {/* Delivery */}
          <button
            onClick={() => handleModeSelect('delivery')}
            disabled={settings && !settings.accepts_delivery}
            className={`w-full rounded-2xl p-5 flex items-center justify-between transition-all ${
              settings && !settings.accepts_delivery 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:scale-[1.02] active:scale-[0.98]'
            }`}
            style={{ 
              backgroundColor: '#262626',
              border: `2px solid ${primaryColor}40`
            }}
            data-testid="delivery-mode-button"
          >
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: primaryColor }}
              >
                <Truck className="w-6 h-6" style={{ color: bgColor }} />
              </div>
              <div className="text-left">
                <div className="text-lg font-bold" style={{ color: primaryColor }}>Delivery</div>
                <div className="text-xs opacity-75">
                  {settings?.estimated_delivery_time || 'Recibilo en tu domicilio'}
                </div>
              </div>
            </div>
            <div className="text-2xl" style={{ color: primaryColor }}>→</div>
          </button>

          {/* Take Away */}
          <button
            onClick={() => handleModeSelect('takeaway')}
            disabled={settings && !settings.accepts_takeaway}
            className={`w-full rounded-2xl p-5 flex items-center justify-between transition-all ${
              settings && !settings.accepts_takeaway 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:scale-[1.02] active:scale-[0.98]'
            }`}
            style={{ 
              backgroundColor: '#262626',
              border: `2px solid ${primaryColor}40`
            }}
            data-testid="takeaway-mode-button"
          >
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: primaryColor }}
              >
                <ShoppingBag className="w-6 h-6" style={{ color: bgColor }} />
              </div>
              <div className="text-left">
                <div className="text-lg font-bold" style={{ color: primaryColor }}>Take Away</div>
                <div className="text-xs opacity-75">Retiralo en el local</div>
              </div>
            </div>
            <div className="text-2xl" style={{ color: primaryColor }}>→</div>
          </button>
        </div>

        {/* Zonas de Envío y Métodos de Pago */}
        <div className="max-w-md mx-auto space-y-3">
          {/* Zonas de Envío */}
          {settings?.delivery_zones?.length > 0 && (
            <div className="p-4 rounded-xl" style={{ backgroundColor: '#262626' }}>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4" style={{ color: primaryColor }} />
                <span className="text-sm font-semibold" style={{ color: primaryColor }}>Zonas de Envío</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {settings.delivery_zones.filter(z => z.active).map((zone, i) => (
                  <span 
                    key={i} 
                    className="text-xs px-2 py-1 rounded-full bg-white/10"
                  >
                    {zone.name} {zone.fee > 0 && `(+$${zone.fee})`}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Métodos de Pago */}
          {settings?.payment_methods?.length > 0 && (
            <div className="p-4 rounded-xl" style={{ backgroundColor: '#262626' }}>
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-4 h-4" style={{ color: primaryColor }} />
                <span className="text-sm font-semibold" style={{ color: primaryColor }}>Formas de Pago</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {settings.payment_methods.map((method, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded-full bg-white/10">
                    {method === 'cash' ? 'Efectivo' : method === 'transfer' ? 'Transferencia' : 'Mercado Pago'}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Botón personalizado */}
        {business?.custom_button_text && business?.custom_button_url && (
          <div className="max-w-md mx-auto mt-4">
            <a href={business.custom_button_url} target="_blank" rel="noopener noreferrer"
              className="block w-full text-center py-3 px-6 rounded-xl font-bold text-sm transition-transform hover:scale-105"
              style={{ backgroundColor: primaryColor, color: '#000' }}
              data-testid="custom-link-button"
            >
              {business.custom_button_text}
            </a>
          </div>
        )}

        {/* Redes sociales */}
        {(business?.social_instagram || business?.social_facebook || business?.whatsapp) && (
          <div className="flex justify-center gap-5 mt-5">
            {business.social_instagram && (
              <a href={business.social_instagram} target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity" data-testid="social-instagram-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
              </a>
            )}
            {business.social_facebook && (
              <a href={business.social_facebook} target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity" data-testid="social-facebook-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
              </a>
            )}
            {business.whatsapp && (
              <a href={`https://wa.me/${business.whatsapp}`} target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity" data-testid="social-whatsapp-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomeScreen;
